/**
 * BaseRepository
 * 
 * Abstract base class for all repositories.
 * Provides common CRUD operations to eliminate code duplication.
 * 
 * Follows OOD principles:
 * - Single Responsibility: Database access layer
 * - DRY: Common operations defined once
 * - Open/Closed: Extendable for specialized operations
 */

import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  setDoc, 
  updateDoc,
  deleteDoc, 
  query, 
  where,
  orderBy as firestoreOrderBy,
  limit as firestoreLimit,
  startAfter,
  writeBatch,
  serverTimestamp,
  onSnapshot
} from 'firebase/firestore';

export class BaseRepository {
  /**
   * @param {Firestore} db - Firestore database instance
   * @param {Functions} functions - Firebase Functions instance
   * @param {string} collectionName - Firestore collection name
   * @param {Class} ModelClass - Domain model class (e.g., Property, Mailshot)
   */
  constructor(db, functions, collectionName, ModelClass) {
    if (new.target === BaseRepository) {
      throw new TypeError("Cannot construct BaseRepository instances directly");
    }
    
    this.db = db;
    this.functions = functions;
    this.collectionName = collectionName;
    this.ModelClass = ModelClass;
  }

  /**
   * Get collection reference
   * @returns {CollectionReference}
   */
  getCollection() {
    return collection(this.db, this.collectionName);
  }

  /**
   * Get document reference
   * @param {string} id - Document ID
   * @returns {DocumentReference}
   */
  getDocRef(id) {
    return doc(this.db, this.collectionName, id);
  }

  /**
   * Find a single entity by ID
   * @param {string} id - Entity ID
   * @returns {Promise<Object|null>} - Domain model instance or null
   */
  async findById(id) {
    try {
      const docRef = this.getDocRef(id);
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) {
        return null;
      }
      
      return this.ModelClass.fromFirestore(docSnap);
    } catch (error) {
      console.error(`Error finding ${this.collectionName} by ID:`, error);
      throw error;
    }
  }

  /**
   * Find multiple entities by IDs
   * @param {string[]} ids - Array of entity IDs
   * @returns {Promise<Object[]>} - Array of domain model instances
   */
  async findByIds(ids) {
    if (!ids || ids.length === 0) {
      return [];
    }

    try {
      // Firestore 'in' queries are limited to 10 items
      const batchSize = 10;
      const batches = [];
      
      for (let i = 0; i < ids.length; i += batchSize) {
        const batchIds = ids.slice(i, i + batchSize);
        const q = query(
          this.getCollection(),
          where('__name__', 'in', batchIds)
        );
        batches.push(getDocs(q));
      }
      
      const results = await Promise.all(batches);
      const entities = [];
      
      results.forEach(snapshot => {
        snapshot.docs.forEach(doc => {
          entities.push(this.ModelClass.fromFirestore(doc));
        });
      });
      
      return entities;
    } catch (error) {
      console.error(`Error finding ${this.collectionName} by IDs:`, error);
      throw error;
    }
  }

  /**
   * Find all entities with optional filtering and sorting
   * @param {Object} options - Query options
   * @param {Array} options.where - Array of where clauses [field, operator, value]
   * @param {string} options.orderBy - Field to order by
   * @param {string} options.orderDirection - 'asc' or 'desc'
   * @param {number} options.limit - Maximum number of results
   * @param {DocumentSnapshot} options.startAfter - Document to start after (pagination)
   * @returns {Promise<Object[]>} - Array of domain model instances
   */
  async findAll(options = {}) {
    try {
      const constraints = [];

      // Add where clauses
      if (options.where) {
        options.where.forEach(([field, operator, value]) => {
          constraints.push(where(field, operator, value));
        });
      }

      // Add orderBy
      if (options.orderBy) {
        constraints.push(
          firestoreOrderBy(options.orderBy, options.orderDirection || 'asc')
        );
      }

      // Add startAfter for pagination
      if (options.startAfter) {
        constraints.push(startAfter(options.startAfter));
      }

      // Add limit
      if (options.limit) {
        constraints.push(firestoreLimit(options.limit));
      }

      const q = constraints.length > 0 
        ? query(this.getCollection(), ...constraints)
        : this.getCollection();

      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(doc => this.ModelClass.fromFirestore(doc));
    } catch (error) {
      console.error(`Error finding all ${this.collectionName}:`, error);
      throw error;
    }
  }

  /**
   * Create a new entity
   * @param {Object} data - Entity data
   * @param {string} id - Optional custom ID (if not provided, auto-generated)
   * @returns {Promise<Object>} - Created domain model instance
   */
  async create(data, id = null) {
    try {
      const docRef = id ? this.getDocRef(id) : doc(this.getCollection());
      
      const entityData = {
        ...data,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      await setDoc(docRef, entityData);
      
      // Return the created entity
      const docSnap = await getDoc(docRef);
      return this.ModelClass.fromFirestore(docSnap);
    } catch (error) {
      console.error(`Error creating ${this.collectionName}:`, error);
      throw error;
    }
  }

  /**
   * Update an existing entity
   * @param {string} id - Entity ID
   * @param {Object} data - Fields to update
   * @returns {Promise<Object>} - Updated domain model instance
   */
  async update(id, data) {
    try {
      const docRef = this.getDocRef(id);
      
      const updateData = {
        ...data,
        updatedAt: serverTimestamp(),
      };

      await updateDoc(docRef, updateData);
      
      // Return the updated entity
      const docSnap = await getDoc(docRef);
      return this.ModelClass.fromFirestore(docSnap);
    } catch (error) {
      console.error(`Error updating ${this.collectionName}:`, error);
      throw error;
    }
  }

  /**
   * Delete an entity by ID
   * @param {string} id - Entity ID
   * @returns {Promise<void>}
   */
  async delete(id) {
    try {
      const docRef = this.getDocRef(id);
      await deleteDoc(docRef);
    } catch (error) {
      console.error(`Error deleting ${this.collectionName}:`, error);
      throw error;
    }
  }

  /**
   * Subscribe to real-time updates for a single entity
   * @param {string} id - Entity ID
   * @param {Function} callback - Callback function (entity) => void
   * @returns {Function} - Unsubscribe function
   */
  subscribeById(id, callback) {
    const docRef = this.getDocRef(id);
    
    return onSnapshot(
      docRef,
      (docSnap) => {
        if (docSnap.exists()) {
          const entity = this.ModelClass.fromFirestore(docSnap);
          callback(entity);
        } else {
          callback(null);
        }
      },
      (error) => {
        console.error(`Error in ${this.collectionName} subscription:`, error);
        callback(null, error);
      }
    );
  }

  /**
   * Subscribe to real-time updates for multiple entities
   * @param {Object} options - Query options (same as findAll)
   * @param {Function} callback - Callback function (entities) => void
   * @param {Function} errorCallback - Optional error callback (error) => void
   * @returns {Function} - Unsubscribe function
   */
  subscribeAll(options = {}, callback, errorCallback) {
    const constraints = [];

    // Build query same as findAll
    if (options.where) {
      options.where.forEach(([field, operator, value]) => {
        constraints.push(where(field, operator, value));
      });
    }

    if (options.orderBy) {
      constraints.push(
        firestoreOrderBy(options.orderBy, options.orderDirection || 'asc')
      );
    }

    if (options.limit) {
      constraints.push(firestoreLimit(options.limit));
    }

    const q = constraints.length > 0 
      ? query(this.getCollection(), ...constraints)
      : this.getCollection();

    return onSnapshot(
      q,
      (snapshot) => {
        const entities = snapshot.docs.map(doc =>
          this.ModelClass.fromFirestore(doc)
        );
        callback(entities);
      },
      (error) => {
        console.error(`❌ Error in ${this.collectionName} subscription:`, error);
        if (errorCallback) {
          errorCallback(error);
        } else {
          callback([], error);
        }
      }
    );
  }

  /**
   * Batch write operations
   * @param {Array} operations - Array of {type: 'create'|'update'|'delete', id, data}
   * @returns {Promise<void>}
   */
  async batchWrite(operations) {
    try {
      const batch = writeBatch(this.db);

      for (const op of operations) {
        const docRef = op.id ? this.getDocRef(op.id) : doc(this.getCollection());

        switch (op.type) {
          case 'create':
            batch.set(docRef, {
              ...op.data,
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp(),
            });
            break;

          case 'update':
            batch.update(docRef, {
              ...op.data,
              updatedAt: serverTimestamp(),
            });
            break;

          case 'delete':
            batch.delete(docRef);
            break;

          default:
            throw new Error(`Unknown operation type: ${op.type}`);
        }
      }

      await batch.commit();
    } catch (error) {
      console.error(`Error in batch write for ${this.collectionName}:`, error);
      throw error;
    }
  }
}
